import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Sparkles, TrendingUp, TrendingDown, Minus, AlertCircle, Lightbulb, Activity, Brain, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TrendBadge({ trend, label }) {
  const getIcon = () => {
    if (trend === 'improving') return <TrendingUp className="h-4 w-4" />;
    if (trend === 'declining') return <TrendingDown className="h-4 w-4" />;
    return <Minus className="h-4 w-4" />;
  };
  
  const getColor = () => {
    if (trend === 'improving') return 'bg-green-100 text-green-700';
    if (trend === 'declining') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };
  
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getColor()}`}>
      {getIcon()}
      <span>{label}: {trend}</span>
    </div>
  );
}

function InsightCard({ icon: Icon, title, items, color }) {
  if (!items || items.length === 0) return null;
  
  return (
    <div className="card-soft">
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`h-5 w-5 ${color}`} />
        <h3 className="font-medium text-foreground">{title}</h3>
      </div>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Insights() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchInsights = async (showRefresh) => {
    if (showRefresh) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/insights/weekly`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setInsights(res.data);
    } catch (err) {
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchInsights(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="h-8 w-8 text-primary mx-auto animate-pulse" />
          <p className="mt-2 text-muted-foreground">Analyzing your week...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-safe" data-testid="insights-page">
      <div className="max-w-lg mx-auto px-6 pt-8">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" /><span>Back</span>
          </button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => fetchInsights(true)}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="mt-6 page-enter">
          <div className="flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-primary" />
            <h1 className="font-heading text-3xl font-semibold text-foreground">Weekly Insights</h1>
          </div>
          {insights && (
            <p className="mt-2 text-sm text-muted-foreground">{insights.period}</p>
          )}
        </div>

        {insights && (
          <>
            {/* Summary Card */}
            <div className="mt-6 card-soft bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 page-enter stagger-1" data-testid="summary-card">
              <p className="text-foreground leading-relaxed">{insights.summary}</p>
              
              <div className="mt-4 flex flex-wrap gap-2">
                <TrendBadge trend={insights.mood_trend} label="Mood" />
                <TrendBadge trend={insights.energy_trend} label="Energy" />
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  {insights.data_points} data points
                </span>
              </div>
            </div>

            {/* Top Symptoms & Triggers */}
            {(insights.top_symptoms.length > 0 || insights.top_triggers.length > 0) && (
              <div className="mt-4 grid grid-cols-2 gap-3 page-enter stagger-2">
                {insights.top_symptoms.length > 0 && (
                  <div className="card-soft">
                    <p className="text-xs text-muted-foreground mb-2">Top Symptoms</p>
                    <div className="flex flex-wrap gap-1">
                      {insights.top_symptoms.map((s, i) => (
                        <span key={i} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{s}</span>
                      ))}
                    </div>
                  </div>
                )}
                {insights.top_triggers.length > 0 && (
                  <div className="card-soft">
                    <p className="text-xs text-muted-foreground mb-2">Common Triggers</p>
                    <div className="flex flex-wrap gap-1">
                      {insights.top_triggers.map((t, i) => (
                        <span key={i} className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{t}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Patterns */}
            <div className="mt-4 page-enter stagger-3">
              <InsightCard 
                icon={Brain} 
                title="Patterns Noticed" 
                items={insights.patterns}
                color="text-purple-500"
              />
            </div>

            {/* Recommendations */}
            <div className="mt-4 page-enter stagger-4">
              <InsightCard 
                icon={Lightbulb} 
                title="Recommendations" 
                items={insights.recommendations}
                color="text-amber-500"
              />
            </div>

            {/* Quick Actions */}
            <div className="mt-6 grid grid-cols-2 gap-3 page-enter stagger-5">
              <button
                onClick={() => navigate('/journal')}
                className="card-soft text-left p-4 hover:border-primary/30"
                data-testid="journal-link"
              >
                <Activity className="h-5 w-5 text-rose-500 mb-2" />
                <h3 className="font-medium text-foreground text-sm">Add Journal</h3>
                <p className="text-xs text-muted-foreground">Record thoughts</p>
              </button>
              <button
                onClick={() => navigate('/symptoms')}
                className="card-soft text-left p-4 hover:border-primary/30"
                data-testid="symptoms-link"
              >
                <Activity className="h-5 w-5 text-blue-500 mb-2" />
                <h3 className="font-medium text-foreground text-sm">Log Symptoms</h3>
                <p className="text-xs text-muted-foreground">Track today</p>
              </button>
            </div>
          </>
        )}

        <div className="h-8" />
      </div>
    </div>
  );
}
